import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { 
  Users, UserCheck, ClipboardList, Rocket, MoreVertical, 
  Eye, Briefcase, FileText, Target, UserPlus, CheckCircle,
  Ban, RotateCcw, Trash2, ChevronRight, GripVertical, Send
} from 'lucide-react';

const ClientPipeline = ({ 
  clients, 
  leads = [],
  pendingClients = [],
  onViewClient,
  onManagePrograms,
  onSendIntake,
  onGenerateReport,
  onAssignConsultant,
  onManualOnboard,
  onSuspend,
  onRestore,
  onDelete,
  onUpdateTier,
  getTierLabel,
  getTierBadgeStyle,
  processingAction,
  generatingReport
}) => {
  const [draggedClient, setDraggedClient] = useState(null);
  const [expandedStage, setExpandedStage] = useState(null);

  // Categorize clients into pipeline stages
  const pipelineStages = {
    leads: {
      title: 'Leads',
      icon: Users,
      color: 'blue',
      description: 'Assessment completed',
      items: leads || []
    },
    pendingIntake: {
      title: 'Pending Intake',
      icon: ClipboardList,
      color: 'amber',
      description: 'NDA accepted, awaiting intake form',
      items: clients.filter(c => c.access_status === 'pending_intake')
    },
    awaitingOnboard: {
      title: 'Ready',
      icon: UserCheck,
      color: 'purple',
      description: 'Intake complete, ready for consultation',
      items: clients.filter(c => c.access_status === 'ready' && !c.onboarded)
    },
    active: {
      title: 'Active',
      icon: Rocket,
      color: 'green',
      description: 'Onboarded',
      items: clients.filter(c => c.onboarded && c.access_status !== 'suspended')
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', header: 'bg-blue-100' },
      amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', header: 'bg-amber-100' },
      purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', header: 'bg-purple-100' },
      green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700', header: 'bg-green-100' }
    };
    return colors[color] || colors.blue;
  };

  const handleDragStart = (e, client, stage) => {
    setDraggedClient({ client, fromStage: stage });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, toStage) => {
    e.preventDefault();
    if (draggedClient && draggedClient.fromStage !== toStage) {
      const { client, fromStage } = draggedClient;
      
      if (toStage === 'active' && fromStage === 'awaitingOnboard') {
        onManualOnboard(client.user_id, client.name);
      }
    }
    setDraggedClient(null);
  };

  const renderClientCard = (client, stage, isLead = false) => {
    const colors = getColorClasses(pipelineStages[stage]?.color || 'blue');
    
    return (
      <div
        key={client.user_id || client.assessment_id}
        draggable={!isLead}
        onDragStart={(e) => !isLead && handleDragStart(e, client, stage)}
        className={`p-3 bg-white rounded-lg border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition-all cursor-pointer group ${
          draggedClient?.client?.user_id === client.user_id ? 'opacity-50' : ''
        }`}
        onClick={() => !isLead && onViewClient(client.user_id)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className={`w-8 h-8 rounded-full ${isLead ? 'bg-blue-100' : 'bg-[#39FF14]/10'} flex items-center justify-center flex-shrink-0`}>
              <span className={`text-sm font-semibold ${isLead ? 'text-blue-600' : 'text-[#39FF14]'}`}>
                {(client.name || client.contact_name)?.charAt(0) || '?'}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm text-neutral-900 truncate" title={client.name || client.contact_name}>
                {client.name || client.contact_name}
              </p>
              <p className="text-xs text-neutral-500 truncate" title={client.email || client.contact_email}>
                {client.email || client.contact_email}
              </p>
            </div>
          </div>
          
          {!isLead && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 flex-shrink-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onViewClient(client.user_id); }}>
                  <Eye className="w-4 h-4 mr-2" /> View Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onManagePrograms(client); }}>
                  <Briefcase className="w-4 h-4 mr-2" /> Programs
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSendIntake(client); }}>
                  <Send className="w-4 h-4 mr-2" /> Send Intake
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onGenerateReport(client.user_id, client.name); }}>
                  <FileText className="w-4 h-4 mr-2" /> Report
                </DropdownMenuItem>
                {!client.onboarded && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onManualOnboard(client.user_id, client.name); }} className="text-green-600">
                      <CheckCircle className="w-4 h-4 mr-2" /> Onboard Now
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(client); }} className="text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        {/* Info row */}
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {isLead ? (
            <Badge className="text-xs px-1.5 py-0 bg-blue-100 text-blue-700 font-medium">
              Score: {client.clarity_score || 'N/A'}
            </Badge>
          ) : (
            <>
              {client.tier && (
                <Badge className={`text-xs px-1.5 py-0 ${getTierBadgeStyle(client.tier)}`}>
                  {getTierLabel(client.tier)}
                </Badge>
              )}
              {client.programs?.includes('rapid') && (
                <span className="text-xs" title="R.A.P.I.D.">⚡</span>
              )}
              {client.programs?.includes('podcast_academy') && (
                <span className="text-xs" title="Podcast">🎙️</span>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const totalClients = Object.values(pipelineStages).reduce((sum, stage) => sum + stage.items.length, 0);

  return (
    <Card className="border-neutral-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">Client Pipeline</CardTitle>
            <p className="text-sm text-neutral-500 mt-0.5">Track clients through their journey</p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            {Object.entries(pipelineStages).map(([key, stage]) => {
              const colors = getColorClasses(stage.color);
              return (
                <div key={key} className="flex items-center gap-1.5">
                  <div className={`w-2.5 h-2.5 rounded-full ${colors.badge.split(' ')[0]}`}></div>
                  <span className="text-neutral-600 font-medium">{stage.items.length}</span>
                  <span className="text-neutral-400 hidden sm:inline">{stage.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Pipeline Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(pipelineStages).map(([stageKey, stage]) => {
            const colors = getColorClasses(stage.color);
            const Icon = stage.icon;
            const isLeadStage = stageKey === 'leads';
            
            return (
              <div
                key={stageKey}
                className={`rounded-xl border ${colors.border} overflow-hidden`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stageKey)}
              >
                {/* Column Header */}
                <div className={`px-3 py-2.5 ${colors.header} border-b ${colors.border}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${colors.text}`} />
                      <span className={`font-semibold text-sm ${colors.text}`}>{stage.title}</span>
                    </div>
                    <Badge className={`${colors.badge} text-xs font-semibold`}>
                      {stage.items.length}
                    </Badge>
                  </div>
                  <p className="text-xs text-neutral-500 mt-0.5">{stage.description}</p>
                </div>
                
                {/* Column Content */}
                <div className={`p-2 space-y-2 ${colors.bg} min-h-[250px] max-h-[500px] overflow-y-auto`}>
                  {stage.items.length === 0 ? (
                    <div className="text-center py-8 text-neutral-400 text-sm">
                      No {stage.title.toLowerCase()}
                    </div>
                  ) : (
                    stage.items.map(item => renderClientCard(item, stageKey, isLeadStage))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ClientPipeline;
